import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import { APIURL } from '../../../config';
import moment from 'moment';
import {
    Typography, Button, List, ListItem, ListItemText,
    IconButton, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, Select, FormControl, InputLabel,
    Chip, CircularProgress, Card,
    RadioGroup, FormControlLabel, Radio, Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';

const DAYS = [
    { key: 'Monday', short: 'Mo' },
    { key: 'Tuesday', short: 'Tu' },
    { key: 'Wednesday', short: 'We' },
    { key: 'Thursday', short: 'Th' },
    { key: 'Friday', short: 'Fr' },
    { key: 'Saturday', short: 'Sa' },
    { key: 'Sunday', short: 'Su' },
];

const REPEAT_UNITS = ['WEEK', 'MONTH'];

const DOSAGE_UNITS = ['mg', 'mcg', 'g', 'ml', 'tablet(s)', 'capsule(s)', 'unit(s)', 'drop(s)', 'puff(s)', 'patch(es)', 'IU'];

const SectionHeader = ({ title }) => (
    <Typography variant="subtitle2" style={{ marginTop: 16, marginBottom: 4, fontWeight: 700, borderBottom: '1px solid #eee', paddingBottom: 4 }}>
        {title}
    </Typography>
);

const emptyForm = () => ({
    drugOption: null,
    dosageAmount: '',
    dosageUnit: '',
    condition: '',
    notes: '',
    startDate: moment().format('YYYY-MM-DD'),
    repeatEvery: '1',
    repeatUnit: 'WEEK',
    repeatOnDays: [],
    timeInput: '',
    timesOfDay: [],
    endMode: 'NONE',
    endDate: '',
    maxOccurrences: '',
});

function MedicationDetailModal({ med, open, onClose }) {
    if (!med) return null;
    const repeatOn = (() => { try { return JSON.parse(med.repeatOn || '[]'); } catch { return []; } })();
    const timeOfDay = (() => { try { return JSON.parse(med.timeOfDay || '[]'); } catch { return []; } })();
    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle>{med.brand_name || med.generic_name || '(unnamed)'}</DialogTitle>
            <DialogContent>
                {med.dosage && (
                    <Typography variant="body2" style={{ marginBottom: 6 }}>
                        <span style={{ color: '#666' }}>Dosage:</span> {med.dosage}
                    </Typography>
                )}
                {med.condition && (
                    <Typography variant="body2" style={{ marginBottom: 6 }}>
                        <span style={{ color: '#666' }}>Condition:</span> {med.condition}
                    </Typography>
                )}
                {med.startDate && (
                    <Typography variant="body2" style={{ marginBottom: 6 }}>
                        <span style={{ color: '#666' }}>Start Date:</span> {moment(med.startDate).format('MM/DD/YYYY')}
                    </Typography>
                )}
                {med.endDate && (
                    <Typography variant="body2" style={{ marginBottom: 6 }}>
                        <span style={{ color: '#666' }}>End Date:</span> {moment(med.endDate).format('MM/DD/YYYY')}
                    </Typography>
                )}
                {med.repeatEvery && med.repeatUnit && (
                    <Typography variant="body2" style={{ marginBottom: 6 }}>
                        <span style={{ color: '#666' }}>Repeat:</span> Every {med.repeatEvery} {med.repeatUnit}
                    </Typography>
                )}
                {repeatOn.length > 0 && (
                    <Typography variant="body2" style={{ marginBottom: 6 }}>
                        <span style={{ color: '#666' }}>Days:</span> {repeatOn.join(', ')}
                    </Typography>
                )}
                {timeOfDay.length > 0 && (
                    <Typography variant="body2" style={{ marginBottom: 6 }}>
                        <span style={{ color: '#666' }}>Times:</span> {timeOfDay.join(', ')}
                    </Typography>
                )}
                {med.notes && (
                    <Typography variant="body2" style={{ marginBottom: 6 }}>
                        <span style={{ color: '#666' }}>Notes:</span> {med.notes}
                    </Typography>
                )}
                {med.addedOn && (
                    <Typography variant="body2" style={{ marginBottom: 6 }}>
                        <span style={{ color: '#666' }}>Added On:</span> {moment(med.addedOn).format('MM/DD/YYYY')}
                    </Typography>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

function MedicationSection({ medications, customer_id, addedBy, onAdd, onDelete }) {
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailMed, setDetailMed] = useState(null);
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(null);
    const [drugOptions, setDrugOptions] = useState([]);
    const [drugLoading, setDrugLoading] = useState(false);
    const debounceRef = useRef(null);

    const set = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const handleDrugSearch = useCallback((inputValue) => {
        clearTimeout(debounceRef.current);
        if (!inputValue || inputValue.length < 3) { setDrugOptions([]); return; }
        debounceRef.current = setTimeout(async () => {
            setDrugLoading(true);
            try {
                const res = await axios.get(APIURL + `/customer_medication/suggestions`, { params: { searchTerm: inputValue } });
                setDrugOptions(res.data.map(d => ({
                    id: d.id,
                    label: d.manufacturer_name ? `${d.brand_name} (${d.manufacturer_name})` : d.brand_name,
                    brand_name: d.brand_name,
                })));
            } catch (err) {
                console.error(err);
            }
            setDrugLoading(false);
        }, 300);
    }, []);

    const toggleDay = (dayKey) => {
        setForm(prev => ({
            ...prev,
            repeatOnDays: prev.repeatOnDays.includes(dayKey)
                ? prev.repeatOnDays.filter(d => d !== dayKey)
                : [...prev.repeatOnDays, dayKey]
        }));
    };

    const addTime = () => {
        const trimmed = (form.timeInput || '').trim();
        const m = moment(trimmed, ['HH:mm', 'H:mm'], true);
        if (!m.isValid()) { alert('Please enter time in HH:mm (e.g., 09:00)'); return; }
        const normalized = m.format('HH:mm');
        setForm(prev => ({
            ...prev,
            timesOfDay: prev.timesOfDay.includes(normalized) ? prev.timesOfDay : [...prev.timesOfDay, normalized].sort(),
            timeInput: ''
        }));
    };

    const removeTime = (time) => {
        setForm(prev => ({ ...prev, timesOfDay: prev.timesOfDay.filter(t => t !== time) }));
    };

    const validate = () => {
        if (!form.drugOption) { alert('Please select a medication.'); return false; }
        if (!form.dosageAmount) { alert('Please enter a dosage amount.'); return false; }
        if (!form.dosageUnit) { alert('Please select a dosage unit.'); return false; }
        if (!form.startDate) { alert('Please enter a start date.'); return false; }
        const re = parseInt(form.repeatEvery, 10);
        if (isNaN(re) || re <= 0) { alert('Repeat every must be a positive number.'); return false; }
        if (form.repeatUnit === 'WEEK' && form.repeatOnDays.length === 0) { alert('Please select at least one day.'); return false; }
        if (form.timesOfDay.length === 0) { alert('Please add at least one time of day.'); return false; }
        if (form.endMode === 'DATE' && !form.endDate) { alert('Please enter an end date.'); return false; }
        if (form.endMode === 'COUNT') {
            const mo = parseInt(form.maxOccurrences, 10);
            if (isNaN(mo) || mo <= 0) { alert('Max occurrences must be a positive number.'); return false; }
        }
        return true;
    };

    const handleSave = async () => {
        if (!validate()) return;
        setSaving(true);
        try {
            const payload = {
                customer_id,
                addedBy,
                drugID: form.drugOption?.id || null,
                dosage: [form.dosageAmount, form.dosageUnit].filter(Boolean).join(' '),
                condition: form.condition,
                notes: form.notes,
                startDate: form.startDate,
                repeatUnit: form.repeatUnit,
                repeatEvery: parseInt(form.repeatEvery, 10),
                repeatOn: JSON.stringify(form.repeatOnDays),
                timeOfDay: JSON.stringify(form.timesOfDay),
                endDate: form.endMode === 'DATE' ? form.endDate : null,
                maxOccurrences: form.endMode === 'COUNT' ? parseInt(form.maxOccurrences, 10) : null,
            };
            const res = await axios.post(APIURL + `/customer_medication`, payload);
            onAdd({
                customer_medication_id: res.data?.id || Date.now(),
                brand_name: form.drugOption?.brand_name,
                dosage: payload.dosage,
                condition: form.condition,
                notes: form.notes,
                startDate: form.startDate,
                repeatEvery: payload.repeatEvery,
                repeatUnit: payload.repeatUnit,
                repeatOn: payload.repeatOn,
                timeOfDay: payload.timeOfDay,
                endDate: payload.endDate,
                addedOn: new Date().toISOString(),
            });
            setForm(emptyForm());
            setDialogOpen(false);
        } catch (err) {
            console.error(err);
        }
        setSaving(false);
    };

    const handleDelete = async (e, id) => {
        e.stopPropagation();
        setDeleting(id);
        try {
            await axios.delete(APIURL + `/customer_medication/${id}`);
            onDelete(id);
        } catch (err) {
            console.error(err);
        }
        setDeleting(null);
    };

    return (
        <Card elevation={3} style={{ marginBottom: 5, padding: 12, flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: '10px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <Typography variant="subtitle1" fontWeight="bold">MEDICATIONS</Typography>
                <Button size="small" startIcon={<AddIcon />} variant="outlined" onClick={() => setDialogOpen(true)}>
                    Add
                </Button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
                {medications.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">No medications recorded.</Typography>
                ) : (
                    <List dense disablePadding style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                        {medications.map((med, i) => (
                            <ListItem
                                key={med.customer_medication_id || i}
                                disablePadding
                                onClick={() => setDetailMed(med)}
                                sx={{
                                    px: 1,
                                    borderRadius: 1,
                                    border: '1px solid #e8e8e8',
                                    cursor: 'pointer',
                                    bgcolor: '#fafafa',
                                    transition: 'background-color 0.15s ease, border-color 0.15s ease',
                                    '&:hover': { bgcolor: '#EBF5FB', borderColor: '#90CAF9' },
                                }}
                            >
                                <ListItemText
                                    primary={med.brand_name || med.generic_name || '(unnamed)'}
                                    secondary={med.addedOn ? moment(med.addedOn).format('MM/DD/YYYY') : ''}
                                    primaryTypographyProps={{ fontWeight: 500 }}
                                />
                                <ChevronRightIcon fontSize="small" sx={{ color: 'text.disabled', mr: 0.5 }} />
                                <IconButton size="small" onClick={(e) => handleDelete(e, med.customer_medication_id)} disabled={deleting === med.customer_medication_id}>
                                    {deleting === med.customer_medication_id ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" color='error' />}
                                </IconButton>
                            </ListItem>
                        ))}
                    </List>
                )}
            </div>

            <MedicationDetailModal med={detailMed} open={!!detailMed} onClose={() => setDetailMed(null)} />

            <Dialog open={dialogOpen} onClose={() => { setDialogOpen(false); setDrugOptions([]); }} maxWidth="sm" fullWidth>
                <DialogTitle>Add Medication</DialogTitle>
                <DialogContent>
                    {/* Medication Name */}
                    <Autocomplete
                        options={drugOptions}
                        loading={drugLoading}
                        value={form.drugOption}
                        onChange={(_, newValue) => set('drugOption', newValue)}
                        onInputChange={(_, inputValue) => handleDrugSearch(inputValue)}
                        getOptionLabel={o => o?.label || ''}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        noOptionsText="Type 3+ characters to search"
                        filterOptions={x => x}
                        size="small"
                        style={{ marginTop: 10 }}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Medication Name"
                                required
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {drugLoading ? <CircularProgress size={16} /> : null}
                                            {params.InputProps.endAdornment}
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />

                    {/* Dosage Amount + Unit */}
                    <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
                        <TextField
                            label="Amount"
                            size="small"
                            variant="outlined"
                            value={form.dosageAmount}
                            onChange={e => set('dosageAmount', e.target.value.replace(/[^0-9.]/g, ''))}
                            inputProps={{ inputMode: 'decimal' }}
                            style={{ flex: 1 }}
                        />
                        <FormControl size="small" style={{ flex: 1.5 }}>
                            <InputLabel>Unit</InputLabel>
                            <Select label="Unit" value={form.dosageUnit} onChange={e => set('dosageUnit', e.target.value)}>
                                {DOSAGE_UNITS.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </div>

                    {/* Condition */}
                    <TextField
                        label="Condition Medication taken for"
                        fullWidth
                        size="small"
                        variant="outlined"
                        value={form.condition}
                        onChange={e => set('condition', e.target.value)}
                        style={{ marginTop: 10 }}
                    />

                    {/* Notes */}
                    <TextField
                        label="Notes"
                        fullWidth
                        size="small"
                        variant="outlined"
                        multiline
                        minRows={2}
                        value={form.notes}
                        onChange={e => set('notes', e.target.value)}
                        style={{ marginTop: 10 }}
                    />

                    <SectionHeader title="Schedule" />

                    {/* Start Date */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
                        <Typography variant="body2" style={{ fontWeight: 600, minWidth: 70 }}>Start date</Typography>
                        <TextField
                            type="date"
                            size="small"
                            variant="outlined"
                            value={form.startDate}
                            onChange={e => set('startDate', e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </div>

                    {/* Repeat every N + Unit */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginTop: 10 }}>
                        <Typography variant="body2" style={{ fontWeight: 600, minWidth: 70 }}>Repeat every</Typography>
                        <TextField
                            size="small"
                            variant="outlined"
                            value={form.repeatEvery}
                            onChange={e => set('repeatEvery', e.target.value.replace(/[^0-9]/g, ''))}
                            inputProps={{ inputMode: 'numeric' }}
                            style={{ width: 70 }}
                        />
                        <FormControl size="small" style={{ width: 110 }}>
                            <Select value={form.repeatUnit} onChange={e => set('repeatUnit', e.target.value)}>
                                {REPEAT_UNITS.map(u => <MenuItem key={u} value={u}>{u}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </div>

                    {/* Repeat On days */}
                    <div style={{ marginTop: 12 }}>
                        <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 6 }}>Repeat on</Typography>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                            {DAYS.map(d => (
                                <Chip
                                    key={d.key}
                                    label={d.short}
                                    size="small"
                                    color={form.repeatOnDays.includes(d.key) ? 'primary' : 'default'}
                                    onClick={() => toggleDay(d.key)}
                                    clickable
                                />
                            ))}
                        </div>
                    </div>

                    {/* Times of day */}
                    <div style={{ marginTop: 12 }}>
                        <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 6 }}>Time(s) of day</Typography>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                            <TextField
                                label="Add time (HH:mm)"
                                size="small"
                                variant="outlined"
                                value={form.timeInput}
                                onChange={e => set('timeInput', e.target.value)}
                                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addTime(); } }}
                                style={{ flex: 1 }}
                            />
                            <Button variant="contained" size="small" onClick={addTime}>Add</Button>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                            {form.timesOfDay.map(time => (
                                <Chip key={time} label={`${time} ✕`} size="small" onClick={() => removeTime(time)} />
                            ))}
                        </div>
                    </div>

                    {/* End options */}
                    <div style={{ marginTop: 14 }}>
                        <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 6 }}>End options</Typography>
                        <RadioGroup row value={form.endMode} onChange={e => { set('endMode', e.target.value); set('maxOccurrences', ''); }}>
                            <FormControlLabel value="NONE" control={<Radio size="small" />} label="Never" />
                            <FormControlLabel value="DATE" control={<Radio size="small" />} label="Date" />
                            <FormControlLabel value="COUNT" control={<Radio size="small" />} label="Occurrences" />
                        </RadioGroup>
                        {form.endMode === 'DATE' && (
                            <TextField
                                type="date"
                                size="small"
                                variant="outlined"
                                value={form.endDate}
                                onChange={e => set('endDate', e.target.value)}
                                InputLabelProps={{ shrink: true }}
                                style={{ marginTop: 8 }}
                            />
                        )}
                        {form.endMode === 'COUNT' && (
                            <TextField
                                label="Occurrences"
                                size="small"
                                variant="outlined"
                                value={form.maxOccurrences}
                                onChange={e => set('maxOccurrences', e.target.value.replace(/[^0-9]/g, ''))}
                                inputProps={{ inputMode: 'numeric' }}
                                style={{ marginTop: 8, width: 140 }}
                            />
                        )}
                    </div>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
                    <Button onClick={handleSave} variant="contained" disabled={saving}>
                        {saving ? <CircularProgress size={20} /> : 'Add Medication'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Card>
    );
}

export default MedicationSection;
